import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import {
  CustomInput,
  FormRef,
  Overlay,
  OverlayContainer,
  ScrollerService,
  useApi,
  useBookingService,
  useConfig,
  useExternalSearchParams,
  useTranslation
} from '@ibe/components';
import { Button, Col, Row } from 'reactstrap';
import { useNavigate } from 'react-router';
import { useMount } from 'react-use';
import {
  ApiBaseData,
  ApiBookedItem,
  ApiChangeComponentsRequest,
  ApiComponentType,
  ApiInsurance,
  ApiItemType,
  ApiPaymentOption,
  ApiPreparePaymentRequestFromJSON,
  ApiTraveler,
  ApiTravelerToJSON,
  ApiTravelerType
} from '@ibe/api';
import {
  checkoutStepEvent,
  LoggerFactory,
  MaskBookingService,
  SessionStorage,
  SessionStoragePersistence
} from '@ibe/services';
import { CheckoutStep, CheckoutStepComponentProps } from '../../../Models/CheckoutStep';
import fallback from '../../../Translations/generated/Checkout.de.json';
import Keys from '../../../Translations/generated/Checkout.de.json.keys';
import ContentContainer from '../../../Components/ContentContainer';
import ProgressbarWrapper from './ProgressBarWrapper';
import CheckoutPageUrl from '../CheckoutPageUrl';
import { FormItemsProps } from '../../../Config/useTravellerFormConfigProductIBE';
import buildValidationSchema, { getAge } from './externals';
import BookingNoteAndTermsWrapper, {
  RenderTermsElementPropsType,
  TermType
} from './BookingNoteAndTerms';
import TravelerForms from './TravelerForms';
import ErrorWrapper from './ErrorWrapper';
import PageUrl from '../../../Models/PageUrl';
import StickySummary from './StickySummary';
import { SessionStorageType } from '../../../Components/Search';
import { productSearchSessionKey, productSearchSessionTempKey } from '../../../Config/sessionkeys';
import { FlightAndAccommodationSummaryHeader } from './FlightAndAccommodationSummary';
import useThgConfig from '../../../Hooks/useThgConfig';
import {
  Api,
  ApiNewsletterSubscriptionRequest,
  ApiNewsletterSubscriptionRequestToJSON
} from '../../../../api';
import useSearchApi from '../../../Util/useSearchApi';
import type { ValidationResult } from './LoyaltyCard';
import LoyaltyCard from './LoyaltyCard';
import { useAffiliateContext } from '../../../Util/AffiliateProvider';
import {
  BUS_SESSION_STORAGE_KEYS,
  TAXI_30KM_CODE,
  travelersSameAddressForms
} from '../../Util/utils';
import { Element as ScrollTo } from 'react-scroll';
import type { ApiInsuranceUnitExtended } from './Insurance/InsuranceContainer';
import InsuranceContainer from './Insurance/InsuranceContainer';
import { ThgConfigModel } from '../../../Config/ThgConfigModel';
import { TFunction } from 'react-i18next';
import { getExtras } from './OptionalOffers/additionals';
import { stepSessionStorage } from '../../../Util/globals';
import { BOOKINGID_STORAGE_KEY, DOI_KEYS, SIGNUP_SOURCES } from '../../../Util/constant';
import { workflowContext } from '../../../contexts';
import { DEFAULT_TOUR_OPERATOR, TOUR_OPERATOR_TTAG } from './StickySummary/PriceOverviewHeader';
import { Tenant } from '../../../Translations';

const logger = LoggerFactory.get('Traveler');
const scrollElementError = 'travelerErrorScroller';
const scrollLoyaltyCardError = 'scrollLoyaltyCardError';
const scrollInsuranceError = 'scrollInsuranceError';
export const ibeFlightClassName = 'ibe__flight';
export const scrollElementErrorTravelerBase = 'travelerErrorScroller';
const productSessionStorage = new SessionStorage<SessionStorageType>(productSearchSessionKey);
const sessionStorageTemp = new SessionStorage<SessionStorageType>(productSearchSessionTempKey);

const Traveler = observer(function Traveler(props: CheckoutStepComponentProps) {
  const { store, rerouteUrl, navBarOffset, productIBEMode, isWhitelabel } = props;
  const context = useExternalSearchParams();
  const thgConfig = useThgConfig();
  const { t } = useTranslation('Checkout', fallback);
  const config = useConfig();
  const navigate = useNavigate();
  const workflow = useContext(workflowContext);
  const api = useApi();
  const bs = useBookingService();
  const booking = bs.booking;
  const bookingTravelers = booking?.travelers;
  const formRefs = useRef<Array<FormRef<Record<string, unknown>>>>([]);
  const formElRef = useRef<HTMLFormElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState<string | undefined>(undefined);
  const [bookedClicked, setBookedClicked] = useState<boolean>(false);
  const [cardNumber, setCardNumber] = useState<string | null>(null);
  const [cardValid, setCardValid] = useState<ValidationResult>('NOT_SET');
  const [error, setError] = useState<string | undefined>(undefined);
  const [travelers, setTravelers] = useState<ApiTraveler[]>(bookingTravelers || []);
  const [sameAddressForms, setSameAddressForms] = useState<ApiTraveler[]>(
    travelersSameAddressForms.get() || []
  );
  const [externalData, setExternalData] = useState<{
    COUNTRIES: ApiBaseData[];
    SALUTATIONS: ApiBaseData[];
    NATIONALITIES: ApiBaseData[];
  }>({ COUNTRIES: [], SALUTATIONS: [], NATIONALITIES: [] });
  const [selectedPaymentOption, setSelectedPaymentOption] = useState<ApiPaymentOption>(
    ApiPaymentOption.INVOICE
  );

  const [showInsuranceComponent, setShowInsuranceComponent] = useState<boolean>(false);
  const [skipReloadInsurances, setSkipReloadInsurances] = useState<boolean>(false);

  const firstAdult = useMemo(() => {
    return travelers.find(traveler => traveler.type === ApiTravelerType.ADULT) || travelers[0];
  }, [travelers]);

  const components = useMemo(() => {
    return bs?.packageCart?.packageModel?.packageDetails?.[0]?.components ?? [];
  }, [bs?.packageCart?.packageModel?.packageDetails]);

  const extras = useMemo(() => {
    return getExtras(components).filter(e => e.component.selectableItems.length !== 0);
  }, [components]);

  const searchApi = useSearchApi();

  const showLoyaltyCard = !!useAffiliateContext().metaContent?.content?.loyaltyCard;

  store.setTravelerAssignmentValidationSchema(buildValidationSchema(bs, t));
  const selectedPostCode = sessionStorage.getItem(BUS_SESSION_STORAGE_KEYS.selectedPostCode);
  const busStopSelection = sessionStorage.getItem(BUS_SESSION_STORAGE_KEYS.busStopSelection);

  const { handleError, terms, toggleCheckBox, tourOperator } = useTermAndLegal();

  const isTTAG = tourOperator === TOUR_OPERATOR_TTAG;
  let enableCCPayment = isCCPaymentEnable(config.tenant, thgConfig, isWhitelabel, isTTAG);
  const enableCCPaymentForWhiteLabel = !!useAffiliateContext().metaContent?.content
    ?.enableCCPayment;

  if (isWhitelabel && enableCCPayment) enableCCPayment = enableCCPaymentForWhiteLabel;

  useMount(async () => {
    if (!booking || !booking.bookedItems || booking.bookedItems.length === 0) {
      navigate(PageUrl.HOME);
    }

    const response = await store.loadExternalData(config?.tenant ?? 'DE');
    setExternalData({
      COUNTRIES: response.countries,
      SALUTATIONS: response.salutations,
      NATIONALITIES: response.nationalities
    });

    if (config.outsideElementsContainerId) {
      ScrollerService.scrollTo(config.outsideElementsContainerId, { offset: -100 });
    }
  });

  useEffect(() => {
    (async () => {
      if (!skipReloadInsurances && bs.packageCart && bs.packageCart.packageModel.packageDetails) {
        const insuranceComp = bs.packageCart.packageModel.packageDetails[0].components.find(
          c => c.itemType === ApiItemType.INSURANCE && c.componentType === ApiComponentType.OPTIONAL
        );
        if (insuranceComp) {
          try {
            const request: ApiChangeComponentsRequest = {
              startDate: insuranceComp.startDate,
              modifiedComponentId: insuranceComp.id,
              checkComponentIds: [insuranceComp.id]
            };

            await bs.reloadComponents(request);

            const reloadedInsuranceComp =
              bs.packageCart &&
              bs.packageCart.packageModel.packageDetails &&
              bs.packageCart.packageModel.packageDetails[0].components.find(
                c =>
                  c.itemType === ApiItemType.INSURANCE &&
                  c.componentType === ApiComponentType.OPTIONAL
              );

            const apiInsurance = reloadedInsuranceComp?.selectableItems as ApiInsurance[];

            let hasNoInsurance = false,
              hasWithDeductible = false,
              hasWithoutDeductible = false;

            apiInsurance.forEach(i =>
              i.units.forEach(u => {
                const extendedMetaContent = (u as ApiInsuranceUnitExtended).metaInfo
                  ?.contentByMetaType?.insurance?.content;

                if (extendedMetaContent) {
                  hasNoInsurance = extendedMetaContent.isNoInsurance ? true : hasNoInsurance;
                  hasWithoutDeductible = extendedMetaContent.isRecommendation
                    ? true
                    : hasWithoutDeductible;
                  hasWithDeductible =
                    extendedMetaContent.isRecommendation && !extendedMetaContent.isNoInsurance
                      ? true
                      : hasWithDeductible;
                }
              })
            );

            setShowInsuranceComponent(
              hasNoInsurance && (hasWithDeductible || hasWithoutDeductible)
            );
          } catch (err) {
            logger.log(err);
            setShowInsuranceComponent(false);
          }
        } else {
          setShowInsuranceComponent(false);
        }
      }

      // set to true when selecting an insurance, see InsuranceContainer clickAction
      if (skipReloadInsurances) {
        setSkipReloadInsurances(false);
      }
    })();
  }, [bs.booking?.price.finalPrice]);

  useEffect(() => {
    (async () => {
      const firstTraveler = bs.booking?.travelers.find(tr => tr.id === '1');

      // delete success & error messages on newly entered card number
      // keep the number mismatch warning until "Anwenden" is clicked
      if (cardValid !== 'NOT_SET' && cardValid !== 'NUMBER_MISMATCH') {
        setCardValid('NOT_SET');
      }

      // input clear was clicked && it's not already empty -> update traveler loyaltyCard number
      if (cardNumber === '' && firstTraveler && firstTraveler.bonusCardNumber !== '') {
        await updateLoyaltyCardNumber();
      }
    })();
  }, [cardNumber]);

  const scrollToError = (errorId: string): void => {
    ScrollerService.scrollTo(errorId, { offset: -100 });
  };

  const handleBookingError = (errorMessage: string) => {
    setError(errorMessage);
    scrollToError(scrollElementError);
  };

  const legalChecked = !!terms.find(term => term.type === 'legal')?.checked;
  const legalError = terms.find(term => term.type === 'legal')?.error;
  const emailChecked = !!terms.find(term => term.type === 'email')?.checked;
  const partnerNewsletterChecked = !!terms.find(term => term.type === 'partnernewsLetter')?.checked;
  const partnerNewsletter = useAffiliateContext().metaContent?.content?.partnerNewsletter;

  const book = async (): Promise<void> => {
    const formResults = (
      await Promise.all(formRefs.current.map(ref => ref?.triggerSubmit()))
    ).filter(item => !!item);

    if (showInsuranceComponent) {
      setBookedClicked(true);
      const bookedInsurance = bs.booking?.bookedItems.find(
        item =>
          ApiItemType.INSURANCE === item.itemType &&
          ApiComponentType.OPTIONAL === item.componentType
      );

      if (!bookedInsurance) {
        scrollToError(scrollInsuranceError);
        return;
      }
    }

    const traveler1 = bs.booking?.travelers.find(tr1 => tr1.id === '1');
    if (showLoyaltyCard && traveler1 && traveler1.bonusCardNumber !== cardNumber) {
      setCardValid('NUMBER_MISMATCH');
      scrollToError(scrollLoyaltyCardError);
      return;
    }

    if (!legalChecked) {
      handleError('legal', t(Keys.summaryAcceptTaCError));
    }
    if (formResults.some(result => Object.keys(result).length === 0)) {
      const index = formResults.findIndex(result => Object.keys(result).length === 0);
      scrollToError(scrollElementErrorTravelerBase + index);
      return;
    }
    if (!legalChecked) {
      scrollToError(scrollElementError);
      return;
    }

    const firstAdultResult =
      formResults.find(result => result.id === firstAdult.id) || formResults[0];
    const finalResults = formResults.map(result => {
      if (!!sameAddressForms.find(form => form.id === result.id)) {
        return {
          ...result,
          street: firstAdultResult.street,
          city: firstAdultResult.city,
          zipCode: firstAdultResult.zipCode,
          country: firstAdultResult.country
        };
      } else {
        return { ...result };
      }
    });

    if (selectedPostCode && busStopSelection && JSON.parse(busStopSelection) === TAXI_30KM_CODE) {
      const firstAdultResultForPostCode =
        finalResults?.length > 1 ? (finalResults[1] as FormItemsProps) : undefined;
      const useFirstAddress = !!sameAddressForms.find(
        form => form.id === firstAdultResultForPostCode?.id
      );
      const parsedPostCode: ApiBaseData = JSON.parse(selectedPostCode);
      if (
        useFirstAddress &&
        firstAdultResultForPostCode &&
        firstAdultResultForPostCode.zipCode !== parsedPostCode.code
      ) {
        handleBookingError(t(Keys.errorInvalidPostCode));
        return;
      } else {
        const travelerPostCodeMismatch = finalResults.some(
          tr => tr.zipCode !== parsedPostCode.code
        );
        if (travelerPostCodeMismatch) {
          handleBookingError(t(Keys.errorInvalidPostCode));
          return;
        } else {
          setError(undefined);
        }
      }
    }

    const AllTravelers: ApiTraveler[] = finalResults.map(item => {
      const result = item as FormItemsProps;
      const travelerInfoInBooking = travelers.find(
        (traveler: ApiTraveler) => traveler.id === result.id
      );
      if (travelerInfoInBooking) {
        return ApiTravelerToJSON({
          ...travelerInfoInBooking,
          title: result.title,
          birthDate: result.birthDate,
          age: getAge(result.birthDate),
          salutation: result.salutation,
          communicationDetails: {
            phone: result.phone,
            email: result.email
          },
          firstName: result.firstname,
          lastName: result.lastname,
          nationality: result.nationality,
          address: {
            city: result.city,
            countryCode: result.country,
            postalCode: result.zipCode,
            street: result.street
          },
          bonusCardNumber: travelerInfoInBooking.id === '1' ? traveler1?.bonusCardNumber ?? '' : ''
        });
      }
    });

    setLoading(true);
    checkoutStepEvent.broadcast({
      booking: MaskBookingService.maskBooking(bs.booking),
      item: null,
      step: t(Keys.participant)
    });
    try {
      await bs.updateTravelers(AllTravelers);
      const signupSource =
        (config?.tenant && SIGNUP_SOURCES[config.tenant as keyof typeof SIGNUP_SOURCES]) ??
        SIGNUP_SOURCES.DE; // Default to german
      const doiKey =
        (config?.tenant && DOI_KEYS[config.tenant as keyof typeof DOI_KEYS]) ?? DOI_KEYS.DE;
      const promisesReq = [];
      if (promoCode) promisesReq.push(bs.addPromoCodesToBooking([promoCode]));
      if (emailChecked)
        promisesReq.push(
          subscribeToNewsletter(
            searchApi,
            AllTravelers,
            externalData.SALUTATIONS,
            signupSource,
            undefined,
            doiKey
          )
        );

      if (partnerNewsletterChecked && partnerNewsletter) {
        promisesReq.push(
          subscribeToNewsletter(
            searchApi,
            AllTravelers,
            externalData.SALUTATIONS,
            undefined,
            ((partnerNewsletter as unknown) as string).toUpperCase()
          )
        );
      }
      await Promise.all(promisesReq);

      setError(undefined);
      let url;
      if (bs && bs.booking && bs.booking.id) {
        url = await api.preparePayment(
          bs.booking.id,
          ApiPreparePaymentRequestFromJSON({
            isDeposit: false,
            paymentMethod: selectedPaymentOption
          })
        );
      }
      if (url) {
        const bidSessionStorage = new SessionStoragePersistence<string>();
        bidSessionStorage.useKey(context?.widgetSessionKeyPrefix + BOOKINGID_STORAGE_KEY);
        const bid = new URLSearchParams(url).get('bookingId');
        if (bid) {
          bidSessionStorage.set(bid);
        }
        stepSessionStorage.set('confirmation');
        redirect(url);
      }
    } catch (e) {
      handleBookingError(t(Keys.SummaryBookingError));
    } finally {
      setLoading(false);
    }
  };

  const redirect = (url: string) => {
    if (enableCCPayment && selectedPaymentOption === ApiPaymentOption.CREDITCARD) {
      window.location.href = url;
    } else {
      navigate(PageUrl.CHECKOUT + '/' + (workflow.nextStepSlug ?? ''));
    }
  };
  const goBack = async (): Promise<void> => {
    await updateTravelers();

    navigate(
      productIBEMode?.bookingHasComponents && extras.length > 0
        ? CheckoutPageUrl.EXTRAS
        : CheckoutPageUrl.SUMMARY
    );
  };

  const switchSessionData = (sessionToTemp: boolean) => {
    const copyFrom = sessionToTemp ? productSessionStorage : sessionStorageTemp;
    const copyTo = sessionToTemp ? sessionStorageTemp : productSessionStorage;
    const session = copyFrom.get();
    if (!!session) {
      copyTo.set(session);
      copyFrom.clear();
    }
  };

  const handleProgressbarClick = async (step: CheckoutStep): Promise<void> => {
    await updateTravelers();

    if (step.slug === 'packages') {
      switchSessionData(false);
    }
  };

  const updateLoyaltyCardNumber = async (): Promise<void> => {
    if (cardNumber) {
      setLoading(true);

      const firstTrav = travelers.find(firstT => firstT.id === '1');

      if (firstTrav) {
        const newTrav = { ...firstTrav, bonusCardNumber: cardNumber } as ApiTraveler;

        await bs.updateTraveler(newTrav);

        const isCardNumberValid =
          bs?.booking?.travelers[0].bonusCardNumber !== null &&
          cardNumber === bs?.booking?.travelers[0].bonusCardNumber;

        if (isCardNumberValid) {
          setCardValid('VALID');
        } else {
          setCardValid('ERROR');
        }
        setLoading(false);
      }
    } else {
      setCardValid('NOT_SET');
    }
  };

  const inputClearedLoyaltyCard = () => {
    setCardValid('NOT_SET');
    setCardNumber(null);
  };

  const updateTravelers = async (): Promise<void> => {
    try {
      setLoading(true);
      await bs.updateTravelers([...travelers]);
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    travelersSameAddressForms.set(sameAddressForms);
  }, [sameAddressForms]);

  return (
    <div className={`checkout-workflow ${ibeFlightClassName}`}>
      <div className="d-flex justify-content-between mb-4 checkout-nav">
        <Button
          color="secondary"
          className="d-block d-md-none"
          onClick={(): Promise<void> => goBack()}
        >
          {t(Keys.back)}
        </Button>
      </div>
      <ProgressbarWrapper
        onClick={handleProgressbarClick}
        store={store}
        rerouteUrl={rerouteUrl}
        isWhitelabel={isWhitelabel}
      />
      <Row>
        <Col md={8} sm={12} className=" traveler-sticky-main-content">
          <div className="checkout_main_container ">
            <form
              ref={formElRef}
              onSubmit={e => {
                e.preventDefault();
                void book();
              }}
            >
              <OverlayContainer>
                <div className="checkout_main_container--shadow">
                  {bs?.booking?.id && (
                    <FlightAndAccommodationSummaryHeader store={store} booking={booking} />
                  )}
                  <TravelerForms
                    travelers={travelers}
                    setTravelers={setTravelers}
                    formRefs={formRefs}
                    sameAddressForms={sameAddressForms}
                    setSameAddressForms={setSameAddressForms}
                    bs={bs}
                    externalData={externalData}
                    initialValues={bookingTravelers?.map((traveler: ApiTraveler) => ({
                      title: traveler.title ?? '',
                      firstname: traveler.firstName ?? '',
                      lastname: traveler.lastName ?? '',
                      salutation: traveler.salutation ?? '',
                      birthDate: traveler.birthDate ?? '',
                      nationality: traveler.nationality ?? '',
                      city: traveler.address?.city ?? '',
                      country: traveler.address?.countryCode ?? '',
                      street: traveler.address?.street ?? '',
                      zipCode: traveler.address?.postalCode ?? '',
                      email: traveler.communicationDetails?.email ?? '',
                      phone: traveler.communicationDetails?.phone ?? ''
                    }))}
                  />
                </div>

                {showInsuranceComponent && (
                  <ScrollTo name={scrollInsuranceError}>
                    <ContentContainer>
                      <InsuranceContainer
                        bookedClicked={bookedClicked}
                        clickAction={setSkipReloadInsurances}
                      />
                    </ContentContainer>
                  </ScrollTo>
                )}
                {showLoyaltyCard && (
                  <ScrollTo name={scrollLoyaltyCardError}>
                    <LoyaltyCard
                      cardNumber={cardNumber}
                      setCardNumber={setCardNumber}
                      onSubmit={updateLoyaltyCardNumber}
                      handleClearClick={inputClearedLoyaltyCard}
                      cardValid={cardValid}
                    />
                  </ScrollTo>
                )}
                <ContentContainer className="paymentOptions">
                  <fieldset>
                    <legend className="traveler-header font-weight-bold mb-5 legend-no-style">
                      {t(Keys.paymentOptions)}
                    </legend>
                    <Row>
                      <Col className={'col-12'}>
                        <ContentContainer className="paymentOptions__container">
                          <div>
                            <CustomInput
                              type="radio"
                              checked={selectedPaymentOption === ApiPaymentOption.INVOICE}
                              label={t(Keys.invoice)}
                              id="invoice"
                              name="payment"
                              className={'iso__checkout__paymentOption'}
                              onChange={() => setSelectedPaymentOption(ApiPaymentOption.INVOICE)}
                            ></CustomInput>
                            &nbsp;&nbsp;
                            {selectedPaymentOption === ApiPaymentOption.INVOICE && (
                              <div className="payment-info-text">
                                <p className="d-none d-md-block">{t(Keys.invoiceInfoDesktop)}</p>
                                <p className="d-md-none">{t(Keys.invoiceInfoMobile)}</p>
                              </div>
                            )}
                          </div>
                        </ContentContainer>

                        {enableCCPayment && (
                          <ContentContainer className="paymentOptions__container">
                            <div>
                              <CustomInput
                                type="radio"
                                checked={selectedPaymentOption === ApiPaymentOption.CREDITCARD}
                                label={t(Keys.creditCard)}
                                id="creditcard"
                                name="payment"
                                className={'iso__checkout__paymentOption'}
                                onChange={() =>
                                  setSelectedPaymentOption(ApiPaymentOption.CREDITCARD)
                                }
                              ></CustomInput>

                              {selectedPaymentOption === ApiPaymentOption.CREDITCARD && (
                                <div className="payment-info-text">
                                  <p className="d-none d-md-block">
                                    {t(Keys.creditCardInfoDesktop)}
                                  </p>
                                  <p className="d-md-none">{t(Keys.creditCardInfoMobile)}</p>
                                </div>
                              )}
                            </div>
                          </ContentContainer>
                        )}
                      </Col>
                      <Col />
                    </Row>
                  </fieldset>
                </ContentContainer>
                <BookingNoteAndTermsWrapper
                  terms={terms}
                  toggleCheckBox={toggleCheckBox}
                  tourOperator={tourOperator}
                  isWhitelabel={isWhitelabel}
                />
                <div className="d-none justify-content-between d-md-flex checkout-nav">
                  <Button color="secondary" onClick={(): Promise<void> => goBack()}>
                    {t(Keys.back)}
                  </Button>
                  <Button className="ml-2" color="primary" type="submit" disabled={loading}>
                    {t(Keys.book)}
                  </Button>
                </div>
                {loading && <Overlay />}
              </OverlayContainer>
              {legalError || bs.hasError || error ? (
                <ErrorWrapper
                  hasTermError={legalError}
                  error={error}
                  scrollElementError={scrollElementError}
                />
              ) : null}
            </form>
          </div>
        </Col>
        <Col sm={12} md={4} className="traveler-sticky-summary-wrapper">
          {bs.booking && (
            <StickySummary
              loading={loading}
              booking={bs.booking}
              next={async (): Promise<void> => {
                formElRef.current?.requestSubmit();
              }}
              rightBottomText={t(Keys.book)}
              disabled={loading}
              navBarOffset={navBarOffset}
              productIBEMode={productIBEMode}
              showPromotionCode
              handleBookingError={handleBookingError}
              travelers={
                travelers.some(traveler => !!traveler.firstName || !!traveler.lastName)
                  ? travelers
                  : bookingTravelers
              }
              isWhitelabel={isWhitelabel}
              store={store}
            />
          )}
        </Col>
      </Row>
    </div>
  );
});

export default Traveler;

const useTermAndLegal = () => {
  const { t } = useTranslation('Checkout', fallback);
  const thgConfig = useThgConfig();
  const showPartnerNewsletter = useAffiliateContext().metaContent?.content?.partnerNewsletter;
  const [terms, setTerms] = useState<RenderTermsElementPropsType[]>([]);
  const bs = useBookingService();

  const bookedPackage = bs?.booking?.bookedItems.find(
    it => it.itemType === ApiItemType.PACKAGE
  ) as ApiBookedItem;

  const toggleCheckBox = (a: TermType, value: boolean) => {
    setTerms(
      terms.map(term => {
        if (term.type !== a) return term;
        return { ...term, checked: value, error: undefined };
      })
    );
  };

  const handleError = (a: TermType, value?: string) => {
    setTerms(
      terms.map(term => {
        if (term.type !== a) return term;
        return { ...term, error: value };
      })
    );
  };
  const seasonContent = bookedPackage?.metaInfo?.contentByMetaType?.season?.content as
    | { tourOperator: string }
    | undefined;

  const tourOperator = seasonContent?.tourOperator ?? DEFAULT_TOUR_OPERATOR;

  const getTourOperatorTranslationsKey = (key: string) => {
    return tourOperator === TOUR_OPERATOR_TTAG ? key + TOUR_OPERATOR_TTAG : key;
  };

  useEffect(() => {
    setTerms([
      {
        type: 'email',
        title: t(Keys.summaryEmailMarketingTitle),
        text: `${t(
          Keys.summaryEmailMarketingText
        )}<a target="_blank" class="font-weight-bold" href="${thgConfig.baseUrl}${t(
          Keys.dataSecurityLink
        )}">${t(Keys.summaryEmailMarketingText2)}</a>`,
        checked: false,
        error: undefined,
        showCheckbox: true
      },
      ...(showPartnerNewsletter ? getPartnerTerms(showPartnerNewsletter, thgConfig, t) : []),
      {
        type: 'legal',
        title: t(Keys.summaryTermsAndConditions),
        text: `${t(
          getTourOperatorTranslationsKey('summaryAcceptTaC')
        )} <a class="font-weight-bold" target="_blank" href="${thgConfig.baseUrl}${t(
          getTourOperatorTranslationsKey('summaryTaCLink')
        )}">${t(getTourOperatorTranslationsKey('summaryTaCLinkText'))}</a> ${t(
          getTourOperatorTranslationsKey('summaryAcceptTac2')
        )}`,
        checked: false,
        error: undefined,
        showCheckbox: true
      }
    ] as RenderTermsElementPropsType[]);
  }, [showPartnerNewsletter]);

  return { terms, toggleCheckBox, handleError, tourOperator };
};
export const GLOBISTA = 'globista';
export const NORMA = 'norma';

const getPartnerTerms = (
  partnerNewsletter: object,
  thgConfig: ThgConfigModel,
  t: TFunction
): RenderTermsElementPropsType[] => {
  if (((partnerNewsletter as unknown) as string) === GLOBISTA) {
    return [
      {
        type: 'partnernewsLetter',
        title: t(Keys.summaryGlobistaMarketingTitle),
        text: `${t(
          Keys.summaryGlobistaMarketingText
        )}<a target="_blank" class="font-weight-bold" href="${
          thgConfig.globistaDataSecurityLink
        }">${t(Keys.summaryGlobistaMarketingText2)}</a>`,
        checked: false,
        error: undefined,
        showCheckbox: true
      }
    ];
  }
  if (((partnerNewsletter as unknown) as string) === NORMA) {
    return [
      {
        type: 'partnernewsLetter',
        title: t(Keys.summaryNormaMarketingTitle),
        text: `${t(
          Keys.summaryNormaMarketingText
        )}<a target="_blank" class="font-weight-bold" href="${thgConfig.normaDataSecurityLink}">${t(
          Keys.summaryNormaMarketingText2
        )}</a>`,
        checked: false,
        error: undefined,
        showCheckbox: true
      }
    ];
  }
  return [];
};

export const subscribeToNewsletter = (
  searchApi: Api,
  travelers: ApiTraveler[],
  salutations: ApiBaseData[],
  signUpSource?: string,
  thirdPartyConsentEmail?: string,
  doiKey?: string
) => {
  const firstTraveler = travelers[0];
  return searchApi.subscribe(
    ApiNewsletterSubscriptionRequestToJSON({
      title: firstTraveler?.title,
      emailAddress: firstTraveler?.communicationDetails?.email,
      firstName: firstTraveler.firstName,
      lastName: firstTraveler.lastName,
      salutation: (
        salutations.find(salutation => salutation.code === firstTraveler.salutation)?.description ||
        ''
      ).trim(),
      signUpSource,
      thirdPartyConsentEmail,
      doiKey
    } as ApiNewsletterSubscriptionRequest)
  );
};

const isCCPaymentEnable = (
  tenant: string | null,
  thgConfig?: ThgConfigModel,
  isWhiteLabel?: boolean,
  isTTAG?: boolean
): boolean | undefined => {
  if (isWhiteLabel) {
    return thgConfig?.ccPaymentConfig?.enableCCPaymentWhitelabel;
  }
  if (isTTAG) {
    return thgConfig?.ccPaymentConfig?.enableCCPaymentTTAG;
  }
  switch (tenant) {
    case Tenant.DE:
      return thgConfig?.ccPaymentConfig?.enableCCPaymentDE;
    case Tenant.AT:
      return thgConfig?.ccPaymentConfig?.enableCCPaymentAT;
    case Tenant.CH:
      return thgConfig?.ccPaymentConfig?.enableCCPaymentCH;
    default:
      return false;
  }
};
